/**
 * Delivery API Proxy
 * Handles communication with Shopify logistics app for delivery functionality
 */

class DeliveryProxy {
  constructor(options = {}) {
    // Use Shopify app proxy endpoint
    this.baseUrl = '/apps/logistics';
    // Metafields to check for special delivery products
    this.specialMetafields = [
      'deliverable_from',
      'deliverable_to',
      'leads_time',
      'special_order_dates'
    ];
  }

  /**
   * Check cart items for special metafields and return array of product handles
   * @returns {Promise<string[]>} Array of product handles that have special metafields
   */
  async getProductsWithSpecialMetafields() {
    try {
      console.log('Delivery proxy - Starting check for products with special metafields');
      console.log('Delivery proxy - Metafields to check:', this.specialMetafields);
      
      // First, try to use metafields exposed via Liquid (most reliable)
      console.log('Delivery proxy - Checking for window.cartItemsWithMetafields...');
      console.log('Delivery proxy - window.cartItemsWithMetafields exists:', !!window.cartItemsWithMetafields);
      console.log('Delivery proxy - window.cartItemsWithMetafields type:', typeof window.cartItemsWithMetafields);
      console.log('Delivery proxy - window.cartItemsWithMetafields value:', window.cartItemsWithMetafields);
      
      // Try Liquid data first, with retries and an event listener to allow Liquid scripts to finish
      let cartItemsFromLiquid = null;

      const waitForMetafieldsOnce = () => new Promise((resolve) => {
        const handler = () => {
          document.removeEventListener('cartMetafieldsReady', handler);
          resolve();
        };
        document.addEventListener('cartMetafieldsReady', handler, { once: true });
        // Safety timeout: resolve after 1000ms if event not fired
        setTimeout(() => resolve(), 1000);
      });

      // Try to hydrate from inline JSON if available
      const hydrateFromJsonScript = () => {
        const jsonTag = document.getElementById('cart-metafields-data') || document.getElementById('cart-metafields-data-backup');
        if (jsonTag) {
          try {
            const parsed = JSON.parse(jsonTag.textContent);
            if (Array.isArray(parsed) && parsed.length) {
              window.cartItemsWithMetafields = parsed;
              return parsed;
            }
          } catch (e) {
            console.warn('Delivery proxy - Failed to parse cart metafields JSON script:', e);
          }
        }
        return null;
      };

      // If not already present, wait for event/DOM ready and retry a few times
      for (let attempt = 1; attempt <= 10 && !cartItemsFromLiquid; attempt++) {
        // Try JSON script first on each attempt
        const parsed = hydrateFromJsonScript();
        if (parsed) {
          cartItemsFromLiquid = parsed;
          console.log(`Delivery proxy - Using Liquid metafields from JSON script (attempt ${attempt})`);
          console.log('Delivery proxy - Liquid data (JSON):', JSON.stringify(cartItemsFromLiquid, null, 2));
          break;
        }

        if (window.cartItemsWithMetafields && Array.isArray(window.cartItemsWithMetafields)) {
          cartItemsFromLiquid = window.cartItemsWithMetafields;
          console.log(`Delivery proxy - Using Liquid metafields (attempt ${attempt})`);
          console.log('Delivery proxy - Liquid data (JSON):', JSON.stringify(cartItemsFromLiquid, null, 2));
          break;
        }

        console.log(`Delivery proxy - Liquid metafields not ready (attempt ${attempt}/10), waiting for cartMetafieldsReady/event/timeout`);
        // Wait for either the event or a short delay before next attempt
        await waitForMetafieldsOnce();
      }
      
      // Log what we ended up with after retries
      console.log('Delivery proxy - window.cartItemsWithMetafields after retries:', window.cartItemsWithMetafields);
      
      if (cartItemsFromLiquid) {
        console.log('Delivery proxy - Using Liquid cart metafields:', JSON.stringify(cartItemsFromLiquid, null, 2));
        console.log('Delivery proxy - Found', cartItemsFromLiquid.length, 'cart item(s) with metafields');
        
        const productsWithMetafields = [];
        
        for (const item of cartItemsFromLiquid) {
          console.log(`Delivery proxy - Checking product "${item.handle}" (ID: ${item.product_id})...`);
          console.log(`Delivery proxy - Metafields for this product:`, item.metafields);
          
          let hasSpecialMetafield = false;
          
          for (const metafieldKey of this.specialMetafields) {
            const value = item.metafields[metafieldKey];
            const hasValue = value !== null && value !== undefined && value !== '' && value !== 'null';
            
            console.log(`Delivery proxy - Checking metafield "${metafieldKey}":`, {
              value: value,
              hasValue: hasValue,
              type: typeof value
            });
            
            if (hasValue) {
              console.log(`Delivery proxy - ✓ Found special metafield "${metafieldKey}" with value:`, value);
              hasSpecialMetafield = true;
              break; // Found one, no need to check others
            }
          }
          
          if (hasSpecialMetafield) {
            console.log(`Delivery proxy - ✓ Product "${item.handle}" has special metafields, adding to list`);
            productsWithMetafields.push(item.handle);
          } else {
            console.log(`Delivery proxy - ✗ Product "${item.handle}" does not have special metafields`);
          }
        }
        
        console.log('Delivery proxy - Check complete. Products with special metafields:', productsWithMetafields.length > 0 ? productsWithMetafields : 'none');
        return productsWithMetafields;
      }
      
      // Fallback: Fetch cart data and check metafields via API
      // This is less reliable than Liquid but works if Liquid data isn't available
      console.log('Delivery proxy - window.cartItemsWithMetafields not available, falling back to API check');
      
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.warn('Delivery proxy - Could not fetch cart data, status:', cartResponse.status);
        return [];
      }

      const cartData = await cartResponse.json();
      if (!cartData.items || cartData.items.length === 0) {
        console.log('Delivery proxy - Cart is empty, no products to check');
        return [];
      }

      console.log('Delivery proxy - Cart has', cartData.items.length, 'item(s)');
      console.log('Delivery proxy - Cart items:', cartData.items.map(item => ({
        id: item.id,
        product_id: item.product_id,
        title: item.product_title,
        handle: item.handle || 'not available'
      })));

      // Get unique product IDs and their handles
      const productMap = new Map();
      cartData.items.forEach(item => {
        if (item.product_id && !productMap.has(item.product_id)) {
          // Try to get handle from item properties or fetch it
          productMap.set(item.product_id, item.handle || null);
          console.log(`Delivery proxy - Mapped product ID ${item.product_id} to handle:`, item.handle || 'will fetch');
        }
      });

      console.log('Delivery proxy - Found', productMap.size, 'unique product(s) to check');

      const productsWithMetafields = [];

      // Check each unique product for metafields
      for (const [productId, handle] of productMap) {
        console.log(`Delivery proxy - Checking product ID ${productId}...`);
        
        // Get handle if not already available
        let productHandle = handle;
        if (!productHandle) {
          console.log(`Delivery proxy - Handle not in cart item, fetching for product ${productId}...`);
          productHandle = await this.getProductHandle(productId);
        }

        if (!productHandle) {
          console.warn(`Delivery proxy - Could not get handle for product ${productId}, skipping`);
          continue;
        }

        console.log(`Delivery proxy - Checking metafields for product handle: "${productHandle}"`);

        // Check if product has any of the special metafields
        const hasMetafield = await this.checkProductMetafields(productHandle);
        if (hasMetafield) {
          console.log(`Delivery proxy - ✓ Product "${productHandle}" has special metafields, adding to list`);
          productsWithMetafields.push(productHandle);
        } else {
          console.log(`Delivery proxy - ✗ Product "${productHandle}" does not have special metafields`);
        }
      }

      console.log('Delivery proxy - Check complete. Products with special metafields:', productsWithMetafields.length > 0 ? productsWithMetafields : 'none');
      return productsWithMetafields;
    } catch (error) {
      console.error('Delivery proxy - Error checking cart items for metafields:', error);
      console.error('Delivery proxy - Error stack:', error.stack);
      return [];
    }
  }

  /**
   * Check if a product has any of the special metafields populated
   * @param {string} productHandle - Product handle to check
   * @returns {Promise<boolean>} True if product has any special metafield with value
   */
  async checkProductMetafields(productHandle) {
    try {
      console.log(`Delivery proxy - Checking metafields for product: "${productHandle}"`);
      
      // Fetch product data - try .js endpoint first (faster, JSON response)
      console.log(`Delivery proxy - Fetching product data from /products/${productHandle}.js`);
      const response = await fetch(`/products/${productHandle}.js`);
      
      if (!response.ok) {
        console.log(`Delivery proxy - Product .js endpoint returned status ${response.status}, trying HTML parsing`);
        // Fallback to HTML page parsing
        return await this.checkProductMetafieldsFromHTML(productHandle);
      }

      const product = await response.json();
      console.log(`Delivery proxy - Product data fetched, checking for metafields...`);
      console.log(`Delivery proxy - Product has metafields property:`, !!product.metafields);
      
      // Check if product has metafields in the response
      // Note: Standard Shopify product.js doesn't include metafields, but some themes/customizations might
      if (product.metafields && Array.isArray(product.metafields)) {
        console.log(`Delivery proxy - Found ${product.metafields.length} metafield(s) in product data`);
        
        for (const metafield of product.metafields) {
          const metafieldKey = metafield.key || (metafield.namespace && metafield.key ? `${metafield.namespace}.${metafield.key}` : null);
          const metafieldValue = metafield.value;
          
          console.log(`Delivery proxy - Checking metafield:`, {
            key: metafieldKey,
            namespace: metafield.namespace,
            value: metafieldValue,
            isSpecial: metafieldKey && this.specialMetafields.includes(metafieldKey),
            hasValue: metafieldValue !== null && metafieldValue !== undefined && metafieldValue !== ''
          });
          
          if (metafieldKey && this.specialMetafields.includes(metafieldKey)) {
            if (metafieldValue !== null && metafieldValue !== undefined && metafieldValue !== '') {
              console.log(`Delivery proxy - ✓ Found special metafield "${metafieldKey}" with value:`, metafieldValue);
              return true;
            } else {
              console.log(`Delivery proxy - Found special metafield "${metafieldKey}" but value is empty/null`);
            }
          }
        }
        
        console.log(`Delivery proxy - No special metafields found in product.js response, trying HTML parsing`);
      } else {
        console.log(`Delivery proxy - Product .js response does not contain metafields array, trying HTML parsing`);
      }

      // If metafields not in .js response, try HTML page
      return await this.checkProductMetafieldsFromHTML(productHandle);
    } catch (error) {
      console.error(`Delivery proxy - Error checking metafields for product ${productHandle}:`, error);
      console.error(`Delivery proxy - Error details:`, {
        message: error.message,
        stack: error.stack
      });
      return false;
    }
  }

  /**
   * Check metafields by parsing product HTML page (fallback method)
   * @param {string} productHandle - Product handle
   * @returns {Promise<boolean>} True if product has any special metafield with value
   */
  async checkProductMetafieldsFromHTML(productHandle) {
    try {
      console.log(`Delivery proxy - Checking metafields from HTML for product: "${productHandle}"`);
      
      // Check if metafield data is available in window object (some themes expose this)
      if (window.productMetafields && window.productMetafields[productHandle]) {
        console.log(`Delivery proxy - Found productMetafields in window object for "${productHandle}"`);
        const metafields = window.productMetafields[productHandle];
        console.log(`Delivery proxy - Window metafields:`, metafields);
        
        for (const key of this.specialMetafields) {
          const value = metafields[key];
          console.log(`Delivery proxy - Checking window metafield "${key}":`, value);
          if (value !== null && value !== undefined && value !== '') {
            console.log(`Delivery proxy - ✓ Found special metafield "${key}" in window object with value:`, value);
            return true;
          }
        }
        console.log(`Delivery proxy - No special metafields found in window object`);
      } else {
        console.log(`Delivery proxy - No productMetafields found in window object`);
      }

      // Try to fetch product page and look for metafield data in script tags or data attributes
      console.log(`Delivery proxy - Fetching product HTML page: /products/${productHandle}`);
      const response = await fetch(`/products/${productHandle}`);
      if (!response.ok) {
        console.log(`Delivery proxy - Product HTML page returned status ${response.status}`);
        return false;
      }

      const html = await response.text();
      console.log(`Delivery proxy - Product HTML fetched, length: ${html.length} characters`);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for JSON-LD script tags with product data
      const jsonLdScripts = doc.querySelectorAll('script[type="application/ld+json"]');
      console.log(`Delivery proxy - Found ${jsonLdScripts.length} JSON-LD script tag(s)`);
      
      for (let i = 0; i < jsonLdScripts.length; i++) {
        const script = jsonLdScripts[i];
        try {
          const data = JSON.parse(script.textContent);
          console.log(`Delivery proxy - Parsed JSON-LD script ${i + 1}, type:`, data['@type']);
          
          if (data['@type'] === 'Product') {
            console.log(`Delivery proxy - Found Product type in JSON-LD, checking for metafields`);
            // Check for metafields in various possible locations
            if (data.metafields) {
              console.log(`Delivery proxy - Found metafields in JSON-LD:`, data.metafields);
              for (const metafield of data.metafields) {
                const key = metafield.key || (metafield.namespace && metafield.key ? `${metafield.namespace}.${metafield.key}` : null);
                const value = metafield.value;
                
                console.log(`Delivery proxy - Checking JSON-LD metafield:`, {
                  key: key,
                  namespace: metafield.namespace,
                  value: value,
                  isSpecial: key && this.specialMetafields.includes(key),
                  hasValue: value !== null && value !== undefined && value !== ''
                });
                
                if (key && this.specialMetafields.includes(key)) {
                  if (value !== null && value !== undefined && value !== '') {
                    console.log(`Delivery proxy - ✓ Found special metafield "${key}" in JSON-LD with value:`, value);
                    return true;
                  }
                }
              }
            } else {
              console.log(`Delivery proxy - No metafields found in JSON-LD Product data`);
            }
          }
        } catch (e) {
          console.log(`Delivery proxy - Error parsing JSON-LD script ${i + 1}:`, e.message);
          // Continue to next script
        }
      }

      // Look for data attributes on product elements
      console.log(`Delivery proxy - Checking for metafields in data attributes`);
      const productElements = doc.querySelectorAll('[data-product-handle], [data-product-id]');
      console.log(`Delivery proxy - Found ${productElements.length} product element(s) with data attributes`);
      
      for (const element of productElements) {
        for (const metafieldKey of this.specialMetafields) {
          const dataAttrName = `data-${metafieldKey.replace(/_/g, '-')}`;
          const value = element.getAttribute(dataAttrName);
          console.log(`Delivery proxy - Checking data attribute "${dataAttrName}":`, value);
          
          if (value !== null && value !== undefined && value !== '') {
            console.log(`Delivery proxy - ✓ Found special metafield "${metafieldKey}" in data attribute with value:`, value);
            return true;
          }
        }
      }

      console.log(`Delivery proxy - ✗ No special metafields found for product "${productHandle}"`);
      return false;
    } catch (error) {
      console.error(`Delivery proxy - Error checking metafields from HTML for product ${productHandle}:`, error);
      console.error(`Delivery proxy - Error details:`, {
        message: error.message,
        stack: error.stack
      });
      return false;
    }
  }

  /**
   * Get product handle from product ID
   * @param {number} productId - Product ID
   * @returns {Promise<string|null>} Product handle or null
   */
  async getProductHandle(productId) {
    try {
      console.log(`Delivery proxy - Fetching handle for product ID ${productId}...`);
      // Try product.js endpoint
      const response = await fetch(`/products/${productId}.js`);
      if (response.ok) {
        const product = await response.json();
        const handle = product.handle || null;
        console.log(`Delivery proxy - Got handle for product ${productId}:`, handle);
        return handle;
      }
      console.log(`Delivery proxy - Could not fetch handle for product ${productId}, status:`, response.status);
      return null;
    } catch (error) {
      console.error(`Delivery proxy - Error getting handle for product ${productId}:`, error);
      return null;
    }
  }

  async getDeliveryWindows(postcode) {
    try {
      console.log('Delivery proxy - Fetching delivery windows for postcode:', postcode);
      
      // Check cart items for special metafields and collect product handles
      const productsWithMetafields = await this.getProductsWithSpecialMetafields();
      
      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      
      // Prepare request body with postcode and pathway
      const requestBody = { 
        postcode: postcode,
        pathway: 'internal__delivery_windows'
      };
      
      // Include products array if any items have special metafields
      if (productsWithMetafields.length > 0) {
        requestBody.products = productsWithMetafields;
        console.log('Delivery proxy - Including products with special metafields:', productsWithMetafields);
      }
      
      console.log('Delivery proxy - Request body:', requestBody);
      
      const response = await fetch(`${this.baseUrl}/windows`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      // Check Content-Type header to detect HTML responses (misconfigured proxy)
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const textPreview = await response.text().then(t => t.substring(0, 200));
        console.error('Delivery proxy - Non-JSON response received:', textPreview);
        throw new Error('Invalid response format from server');
      }

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Ignore JSON parse errors
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Delivery proxy - Received response:', data);
      
      // The response is directly an array of delivery windows
      if (Array.isArray(data) && data.length > 0) {
        return {
          success: true,
          windows: data
        };
      } else if (Array.isArray(data) && data.length === 0) {
        return {
          success: false,
          message: 'No delivery windows available for this postcode',
          windows: []
        };
      } else {
        // Handle other response formats
        return data;
      }
    } catch (error) {
      console.error('Error fetching delivery windows:', error);
      return { success: false, message: 'Network error', error: error.message };
    }
  }

  // Legacy method - now calls getDeliveryWindows and formats response
  async getAvailableDates(postcode) {
    try {
      const windowsData = await this.getDeliveryWindows(postcode);
      
      if (!windowsData.success) {
        return windowsData;
      }

      // Transform the windows data into the expected dates format
      const dates = this.extractDatesFromWindows(windowsData.windows || windowsData.data || []);
      
      return {
        success: true,
        dates: dates
      };
    } catch (error) {
      console.error('Error fetching available dates:', error);
      return { success: false, message: 'Network error' };
    }
  }

  // Legacy method - now extracts time slots from windows data
  async getAvailableTimeSlots(postcode, date) {
    try {
      const windowsData = await this.getDeliveryWindows(postcode);
      
      if (!windowsData.success) {
        return windowsData;
      }

      // Extract time slots for the specific date
      const timeSlots = this.extractTimeSlotsForDate(windowsData.windows || windowsData.data || [], date);
      
      return {
        success: true,
        timeSlots: timeSlots
      };
    } catch (error) {
      console.error('Error fetching available time slots:', error);
      return { success: false, message: 'Network error' };
    }
  }

  // Helper method to extract unique dates from delivery windows
  extractDatesFromWindows(windows) {
    const dateMap = new Map();
    
    windows.forEach(dayData => {
      // Format: each item has date and deliveryWindows array
      const dateStr = dayData.date;
      if (dateStr && dayData.deliveryWindows) {
        // Normalize date to YYYY-MM-DD format
        let dateKey = this.normalizeDate(dateStr);
        
        if (!dateKey) {
          console.warn('Delivery proxy - Could not normalize date:', dateStr);
          return;
        }
        
        if (!dateMap.has(dateKey)) {
          // Count available windows
          const availableWindows = dayData.deliveryWindows.filter(window => window.available);
          const totalCapacity = dayData.deliveryWindows.reduce((sum, window) => sum + (window.capacity || 0), 0);
          
          dateMap.set(dateKey, {
            date: dateKey,
            weekday: dayData.weekday,
            capacity: totalCapacity,
            availableSlots: availableWindows.length,
            deliveryWindows: dayData.deliveryWindows
          });
        }
      }
    });
    
    return Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  }

  // Normalize date string to YYYY-MM-DD format
  normalizeDate(dateStr) {
    if (!dateStr) return null;
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Try to parse as Date and format
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        console.warn('Delivery proxy - Invalid date string:', dateStr);
        return null;
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Delivery proxy - Error normalizing date:', dateStr, error);
      return null;
    }
  }

  // Helper method to extract time slots for a specific date
  extractTimeSlotsForDate(windows, targetDate) {
    const timeSlots = [];
    
    // Normalize the target date to ensure proper matching
    const normalizedTargetDate = this.normalizeDate(targetDate);
    if (!normalizedTargetDate) {
      console.warn('Delivery proxy - Could not normalize target date:', targetDate);
      return timeSlots;
    }
    
    console.log('Delivery proxy - Extracting time slots for date:', normalizedTargetDate);
    console.log('Delivery proxy - Windows count:', windows.length);
    
    windows.forEach((dayData, dayIndex) => {
      // Normalize the date from the window data
      const normalizedWindowDate = this.normalizeDate(dayData.date);
      
      // Format: each item has date and deliveryWindows array
      if (normalizedWindowDate === normalizedTargetDate && dayData.deliveryWindows) {
        console.log('Delivery proxy - Found matching date, deliveryWindows count:', dayData.deliveryWindows.length);
        dayData.deliveryWindows.forEach((window, index) => {
          // Only include available windows
          if (window.available) {
            timeSlots.push({
              id: `${normalizedTargetDate}-slot-${index}`,
              time: window.window,
              display: window.window,
              capacity: window.capacity,
              availableCapacity: window.capacity - window.currentOrders,
              currentOrders: window.currentOrders,
              available: window.available,
              servable: window.servable ?? true,
              testOrder: window.testOrder ?? false
            });
          } else {
            console.log('Delivery proxy - Skipping unavailable window:', window.window);
          }
        });
      } else if (dayIndex < 3) {
        // Log first few dates for debugging
        console.log('Delivery proxy - Date mismatch - Window date:', dayData.date, 'Normalized:', normalizedWindowDate, 'Target:', normalizedTargetDate);
      }
    });
    
    console.log('Delivery proxy - Extracted time slots:', timeSlots.length);
    return timeSlots.sort((a, b) => a.time.localeCompare(b.time));
  }
}

// Create global instance
window.deliveryProxy = new DeliveryProxy();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DeliveryProxy;
}
